import { useMemo } from "react";
import PlayerAvatar from "../common/PlayerAvatar";
import ClubCrest from "../common/ClubCrest";
import NationalityFlags from "../common/NationalityFlags";
import { getBlockDefs, buildRow, buildHero } from "./metricDefs";
import {
  positionLabel,
  positionChipStyle,
  contractLabel,
  footLabel,
  marketValueLabel,
} from "./playerDisplay";

// Compare page — desktop metric grid (frames 1b–1f, CompareGrid prototype).
// ONE shared grid: metric = row, player = column. Column formula
// minmax(0,1.28fr) repeat(n, minmax(0,1fr)); from 3 players on the label
// column compresses to 1.02fr and the type steps down one size — exactly like
// the prototype. Sticky player header row while scrolling.

const A = {
  base: "#2283FF",
  dark: "rgb(24,94,184)",
  tint: "rgba(34,131,255,0.085)",
  ring: "inset 0 0 0 1px rgba(34,131,255,0.22)",
  soft: "rgba(34,131,255,0.38)",
};

const Bar = ({ share, isBest, track = "#E7EDF5", height = 4 }) => (
  <div
    className="w-full overflow-hidden flex justify-end"
    style={{ height, borderRadius: height / 2, background: track }}
  >
    {share !== null && (
      <div
        style={{
          width: `${(share * 100).toFixed(1)}%`,
          height: "100%",
          borderRadius: height / 2,
          background: isBest ? A.base : A.soft,
        }}
      />
    )}
  </div>
);

const PlayerHeaderCell = ({ player, compact, seasonTeamLabel }) => {
  const chip = positionChipStyle(player);
  const pos = positionLabel(player);
  const contract = contractLabel(player.contractExpiration);
  const foot = footLabel(player.foot);
  const mv = marketValueLabel(player.marketValue);
  return (
    <div className="flex flex-col gap-[9px] pl-[14px] min-w-0">
      <div className="flex items-center gap-[11px] min-w-0">
        <div className="relative flex-shrink-0 w-[46px] h-[46px]">
          <PlayerAvatar src={player.imageUrl} name={player.name} className="w-full h-full rounded-full text-[15px]" />
          <ClubCrest name={player.club || ""} logoUrl={player.clubImage} variant="badge" avatarSize={46} />
        </div>
        <div className="flex flex-col gap-[3px] min-w-0">
          <div
            className="font-semibold leading-[1.15] text-[#0F172A]"
            style={{ fontSize: compact ? 15 : 17 }}
            data-no-translate="true"
          >
            {player.name}
          </div>
          <div className="text-[12.5px] text-[#5A6879] truncate" data-no-translate="true">
            {player.club || ""}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-[5px] items-center">
        {pos && (
          <span className="px-[7px] py-[2.5px] rounded-md text-[11px] font-bold tracking-[.02em]" style={chip} data-no-translate="true">
            {pos}
          </span>
        )}
        {player.age !== null && player.age !== undefined && (
          <span className="px-[7px] py-[2.5px] rounded-md bg-[#F1F4F9] text-[#3F4E62] text-[11px] font-semibold" data-no-translate="true">
            {player.age} yrs
          </span>
        )}
        <NationalityFlags
          nationalities={player}
          max={2}
          className="w-[19px] h-[13px] object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(15,23,42,.08)]"
          wrapperClassName="flex items-center gap-1 px-[4px] py-[2px] rounded-md bg-[#F1F4F9]"
        />
        {foot && (
          <span className="px-[7px] py-[2.5px] rounded-md bg-[#F1F4F9] text-[#3F4E62] text-[11px] font-semibold">
            {foot}
          </span>
        )}
        {player.heightCm && (
          <span className="px-[7px] py-[2.5px] rounded-md bg-[#F1F4F9] text-[#3F4E62] text-[11px] font-semibold" data-no-translate="true">
            {player.heightCm} cm
          </span>
        )}
        {/* current market value (TheSports) — rendered ONLY when > 0 */}
        {mv && (
          <span
            className="px-[7px] py-[2.5px] rounded-md bg-[#F1F4F9] text-[#3F4E62] text-[11px] font-semibold"
            title="Market value"
            data-no-translate="true"
          >
            {mv}
          </span>
        )}
      </div>
      {/* whose stats: season + team of THIS player's resolved entry
          (club vs national side, mid-season move) — style of the
          "Contract until" line */}
      {seasonTeamLabel && (
        <div
          className="text-[12px] text-[#5A6879] truncate"
          title={seasonTeamLabel}
          data-no-translate="true"
        >
          {seasonTeamLabel}
        </div>
      )}
      {contract && (
        <div className="text-[12px] text-[#5A6879]">
          {`Contract until ${contract}`}
        </div>
      )}
    </div>
  );
};

const CompareGrid = ({
  players,
  statMaps,
  seasonTeamLabels = [],
  mode,
  notice,
  seasonSelector,
  emptySeasonLabel,
  loadingSeason,
  // expanded/per90 live in the PAGE (shared with the mobile grid) so a
  // loading extra player never unmounts the grid state.
  expanded = {},
  per90 = {},
  onToggleExpanded = () => {},
  onSetPer90 = () => {},
  loadErrors = [],
  onRetry = () => {},
}) => {
  const n = players.length;
  const compact = n > 2;
  const cols = `minmax(0,${compact ? "1.02" : "1.28"}fr) repeat(${n}, minmax(0,1fr))`;
  const numSize = compact ? 15 : 17;
  const heroSize = compact ? 34 : 44;

  const blockDefs = useMemo(() => getBlockDefs(mode), [mode]);
  const hero = useMemo(() => buildHero(statMaps), [statMaps]);

  return (
    // NO overflow-hidden/overflow-clip on the card root: an overflow ancestor
    // becomes the sticky containing block and the player header row would
    // never stick to the viewport. Corner rounding lives on the first/last
    // children instead.
    <div className="bg-white border border-[#E4E9F0] rounded-2xl shadow-[0_1px_2px_rgba(16,24,40,.05),0_22px_44px_-26px_rgba(16,24,40,.28)]">
      {/* header: title + season selector */}
      <div className="flex items-center justify-between gap-4 px-[18px] py-[14px] rounded-t-2xl">
        <div className="flex flex-col gap-[2px]">
          <div className="text-[17px] font-bold tracking-[-.01em] text-[#0F172A]">Statistics</div>
          {loadingSeason ? (
            <div className="flex items-center gap-2">
              <div className="w-[11px] h-[11px] rounded-full border-2 border-[#DCE5F0] border-t-[#2283FF] animate-spin" />
              <span className="text-[12.5px] text-[#5A6879]">Loading season statistics...</span>
            </div>
          ) : (
            <div className="text-[12.5px] text-[#5A6879]">
              Curated metrics per block — expand for the full set
            </div>
          )}
        </div>
        {seasonSelector}
      </div>

      {/* mixed-selection notice (frame 1e) */}
      {notice && (
        <div className="flex items-start gap-[9px] px-[18px] py-[10px] bg-[#EFF4FB] border-t border-[#E1EAF6]">
          <div className="w-[6px] h-[6px] rounded-[2px] bg-[#2283FF] mt-[5px] flex-shrink-0" />
          <div className="text-[12px] leading-[1.45] text-[#35507A] max-w-[760px]">{notice}</div>
        </div>
      )}

      {/* sticky player header row */}
      <div
        className="grid items-end px-[18px] pt-4 pb-[14px] border-t border-[#E9EEF5] sticky top-0 bg-white z-[5]"
        style={{ gridTemplateColumns: cols }}
      >
        <div className="text-[11.5px] font-bold tracking-[.1em] uppercase text-[#8494A8] self-end">Players</div>
        {players.map((p, i) => (
          <PlayerHeaderCell
            key={p.id}
            player={p}
            compact={compact}
            seasonTeamLabel={seasonTeamLabels[i] || null}
          />
        ))}
      </div>

      {/* rating hero row */}
      <div
        className="grid items-center px-[18px] py-4 bg-[#F3F8FF] border-t border-[#E1EBFA]"
        style={{ gridTemplateColumns: cols }}
      >
        <div className="flex flex-col gap-[2px]">
          <div className="text-[15px] font-bold text-[#0F172A]">Rating</div>
          <div className="text-[11.5px] text-[#7A8798]">Ø per match</div>
        </div>
        {hero.map((h, i) => (
          <div
            key={players[i].id}
            className="ml-[14px] px-3 py-2 rounded-[11px] flex flex-col gap-[7px] items-end"
            style={{
              background: h.isBest ? A.tint : "transparent",
              boxShadow: h.isBest ? A.ring : "none",
            }}
          >
            {loadErrors[i] ? (
              <div className="w-full px-[10px] py-3 border border-dashed border-[#CFD9E6] rounded-[10px] text-center text-[11.5px] font-semibold text-[#8494A8] leading-[1.4]">
                <div>Could not load player data</div>
                <button
                  type="button"
                  onClick={() => onRetry(players[i].id)}
                  className="mt-1 text-[11.5px] font-semibold text-[#1B6FD6] hover:text-[#0F5FBF]"
                >
                  Retry
                </button>
              </div>
            ) : h.empty ? (
              <div className="w-full px-[10px] py-3 border border-dashed border-[#CFD9E6] rounded-[10px] text-center text-[11.5px] font-semibold text-[#8494A8] leading-[1.4]">
                {emptySeasonLabel
                  ? `No statistics for ${emptySeasonLabel}`
                  : "No statistics available"}
              </div>
            ) : h.missing ? (
              // Stats exist but the rating is NULL (backend defect F1):
              // plain em-dash, no bar — never the "No statistics" card.
              <div
                className="font-bold tracking-[-.02em] leading-none tabular-nums text-[#A3B0C2]"
                style={{ fontSize: heroSize }}
                data-no-translate="true"
              >
                {h.text}
              </div>
            ) : (
              <>
                <div
                  className="font-bold tracking-[-.02em] leading-none tabular-nums"
                  style={{ fontSize: heroSize, color: h.isBest ? A.dark : "#0F172A" }}
                  data-no-translate="true"
                >
                  {h.text}
                </div>
                <Bar share={h.share} isBest={h.isBest} track="#DDE8F7" height={6} />
              </>
            )}
          </div>
        ))}
      </div>

      {/* metric blocks */}
      {blockDefs.map((block) => {
        const isOpen = !!expanded[block.id];
        const isPer90 = !!per90[block.id];
        const hiddenCount = block.rows.filter((r) => r.extra).length;
        const visibleDefs = block.rows.filter((r) => !r.extra || isOpen);
        return (
          // The last block carries the card's bottom rounding (clipping only
          // itself, never the sticky header), so its block-header background
          // can never poke square corners past the card edge.
          <div key={block.id} className="last:rounded-b-2xl last:overflow-hidden">
            <div className="flex items-center justify-between px-[18px] py-[11px] bg-[#1B6FD6]">
              <div className="text-[11.5px] font-bold tracking-[.1em] uppercase text-white">{block.title}</div>
              {block.p90 && (
                <div className="flex gap-[2px] p-[2px] bg-white/[.12] rounded-lg">
                  <button
                    type="button"
                    onClick={() => onSetPer90(block.id, false)}
                    className={`px-[9px] py-[3px] rounded-md text-[10.5px] font-bold ${
                      !isPer90 ? "bg-white text-[#1B6FD6]" : "text-[#CFE2FA] font-semibold"
                    }`}
                  >
                    Total
                  </button>
                  <button
                    type="button"
                    onClick={() => onSetPer90(block.id, true)}
                    className={`px-[9px] py-[3px] rounded-md text-[10.5px] font-bold ${
                      isPer90 ? "bg-white text-[#1B6FD6]" : "text-[#CFE2FA] font-semibold"
                    }`}
                  >
                    /90
                  </button>
                </div>
              )}
            </div>
            {visibleDefs.map((def) => {
              const row = buildRow(def, statMaps, isPer90);
              return (
                <div
                  key={def.id}
                  className="grid items-center px-[18px] py-[10px] border-t border-[#F1F5F9]"
                  style={{ gridTemplateColumns: cols }}
                >
                  <div className="flex items-baseline gap-[6px] min-w-0">
                    <span className="text-[13.5px] font-medium text-[#1F2A37]">{row.label}</span>
                    {row.note && (
                      <span className="text-[10.5px] text-[#7A8798] font-bold tracking-[.04em]">{row.note}</span>
                    )}
                  </div>
                  {row.cells.map((c, i) => (
                    <div
                      key={players[i].id}
                      className="ml-[14px] px-[10px] py-[5px] rounded-lg flex flex-col gap-[5px] items-end"
                      style={{
                        background: c.isBest ? A.tint : "transparent",
                        boxShadow: c.isBest ? A.ring : "none",
                      }}
                    >
                      <div className="flex items-baseline gap-[5px]">
                        <span
                          className="tabular-nums leading-[1.1]"
                          style={{
                            fontSize: numSize,
                            fontWeight: c.isBest ? 700 : 500,
                            color: c.missing ? "#A3B0C2" : c.isBest ? A.dark : "#0F172A",
                          }}
                          data-no-translate="true"
                        >
                          {c.text}
                        </span>
                        {c.sub && (
                          <span className="text-[11.5px] text-[#7A8798] font-semibold" data-no-translate="true">
                            {c.sub}
                          </span>
                        )}
                      </div>
                      {!c.missing && <Bar share={c.share} isBest={c.isBest} />}
                    </div>
                  ))}
                </div>
              );
            })}
            {hiddenCount > 0 && (
              <div className="px-[18px] py-[9px] border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => onToggleExpanded(block.id)}
                  className="text-[12.5px] font-semibold text-[#1B6FD6] hover:text-[#0F5FBF]"
                >
                  {isOpen ? "Hide extra stats" : `Show all stats (+${hiddenCount})`}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompareGrid;
